import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment } from 'react';
/** The order of a form that stores none: the order of every form before 14.10.2026. */
export const FORM_BLOCKS = [
    'email',
    'lead_fields',
    'terms',
    'registration',
    'account',
    'newsletter',
    'sponsor_contact',
    'daily_reminder',
];
/**
 * A stored order, always complete: unknown names dropped, repeats once, missing blocks after
 * it in the default order (`block_order` on the server reads the same way). The server already
 * translates the names from before D51, so they arrive here as the blocks they became.
 */
export function formBlockOrder(stored) {
    const known = (stored ?? []).filter((entry) => FORM_BLOCKS.includes(entry));
    return [...new Set([...known, ...FORM_BLOCKS])];
}
/**
 * The page's own words wherever an action words nothing, as short as they go (D52). No
 * headline: the form has a legend only when the action words one.
 */
export const FORM_TEXT_DEFAULTS = {
    submit_label: 'Teilnahme abschließen',
    email_label: 'E-Mail-Adresse',
    email_hint: 'Wir schicken Ihnen einen Link zur Bestätigung.',
    terms_label: 'Ich akzeptiere die Teilnahmebedingungen.',
    account_title: 'Kostenloses Kundenkonto anlegen',
    account_hint: 'Ihre Angaben sind dann beim nächsten Türchen schon ausgefüllt. Dafür melden Sie sich einmal an.',
};
/**
 * The address hint of a device that confirmed its address before. Fixed: a hint the action
 * worded for the confirmation link would promise a mail that does not come.
 */
const RECOGNISED_HINT = 'Mit Ihrer hier bestätigten E-Mail-Adresse zählt die Teilnahme sofort.';
/** The word of the terms line that becomes the link to them. */
const TERMS_WORD = 'Teilnahmebedingungen';
/** What the ticked terms box posts; the participant page accepts nothing else (`termsTicked`). */
export const TERMS_ACCEPTED = 'accepted';
/**
 * Input name of a field of the registration form. Prefixed, so no configured key can ever
 * collide with a name of the page itself (`email`, `consent`, `terms`, `konto`).
 */
export function inputName(key) {
    return `angabe_${key}`;
}
/** Id of a field's input, the target of the error summary's links. */
export function fieldInputId(key) {
    return `angabe-${key}`;
}
const problemId = (key) => `fehler-${key}`;
/** A control of the form in the editor: visible as on the page, but out of reach. */
function Still({ edit, children, }) {
    return edit ? (_jsx("span", { inert: true, style: { display: 'contents' }, children: children })) : (_jsx(_Fragment, { children: children }));
}
/**
 * The title of a box: a `<label>` on the page, plain text in the editor, where a click into it
 * edits rather than toggling an inert box.
 */
function BoxTitle({ htmlFor, still, className, children, }) {
    return still ? (_jsx("span", { className: className, children: children })) : (_jsx("label", { htmlFor: htmlFor, className: className, children: children }));
}
/**
 * The star of a required item, bound to the last word so it never starts a line of its own.
 * Hidden from screen readers: they announce the control's `required`, and a read-out "Stern"
 * would say nothing more. The backoffice and the sponsor portal mark their required inputs
 * with it as well, never with "(Pflicht)".
 */
export function Required() {
    return (_jsxs(_Fragment, { children: ['\u00a0', _jsx("span", { "aria-hidden": "true", className: "countdown-required", children: "*" })] }));
}
function FieldInput({ field, value, problem, edit, }) {
    const still = edit !== undefined;
    const editFields = edit?.fields;
    const item = editFields?.item(field.key);
    const id = fieldInputId(field.key);
    const name = inputName(field.key);
    const invalid = problem !== undefined ? true : undefined;
    const describedBy = problem !== undefined ? problemId(field.key) : undefined;
    const refusal = problem !== undefined ? (_jsx("span", { id: problemId(field.key), className: "countdown-form-field__problem", children: problem })) : null;
    /*
      In the editor the label is no <label>: a click into its text edits it rather than reaching
      for the inert input. The required marker stays fixed text.
    */
    const label = (required) => still ? (_jsxs("span", { className: "countdown-form-field__label", children: [editFields ? editFields.label(field.key, field.label) : field.label, required ? _jsx(Required, {}) : null] })) : (_jsxs("label", { htmlFor: id, className: "countdown-form-field__label", children: [field.label, required ? _jsx(Required, {}) : null] }));
    /*
      The chosen field's settings stand after its paragraph, never inside it: a <p> holds no
      controls of its own, and the page's input rules would style them. Chosen by a click and by
      focus entering it, so the keyboard reaches it through the label's editor.
    */
    const paragraph = (className, children) => {
        const classes = item?.className ? `${className} ${item.className}` : className;
        const shown = (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions -- editor only, the keyboard selects through onFocus
        _jsx("p", { className: classes, onClick: item?.onSelect, onFocus: item?.onSelect, children: children }));
        return item?.controls ? (_jsxs(_Fragment, { children: [shown, item.controls] })) : (shown);
    };
    /*
      A box is an answer of the form, never a consent: it is never required, and an unticked one
      is sent as nothing, which the API reads as "nein".
    */
    if (field.type === 'checkbox') {
        return paragraph('countdown-form-field countdown-form-field--check', _jsxs(_Fragment, { children: [_jsx(Still, { edit: still, children: _jsx("input", { type: "checkbox", id: id, name: name, value: "ja", defaultChecked: value !== '', "aria-invalid": invalid, "aria-describedby": describedBy }) }), label(false), refusal] }));
    }
    let input;
    if (field.type === 'select') {
        input = (_jsxs("select", { className: "countdown-form-field__input", id: id, name: name, defaultValue: value === '' ? undefined : value, autoComplete: field.autocomplete ?? undefined, required: field.required, "aria-invalid": invalid, "aria-describedby": describedBy, children: [_jsx("option", { value: "", children: "Bitte w\u00E4hlen" }), field.options.map((option) => (_jsx("option", { value: option, children: option }, option)))] }));
    }
    else if (field.type === 'textarea') {
        input = (_jsx("textarea", { className: "countdown-form-field__input", id: id, name: name, rows: 4, maxLength: field.max_length, required: field.required, "aria-invalid": invalid, "aria-describedby": describedBy, defaultValue: value }));
    }
    else {
        input = (_jsx("input", { className: "countdown-form-field__input", type: "text", inputMode: field.type === 'number' ? 'decimal' : undefined, id: id, name: name, defaultValue: value, autoComplete: field.autocomplete ?? undefined, maxLength: field.max_length, required: field.required, "aria-invalid": invalid, "aria-describedby": describedBy }));
    }
    return paragraph('countdown-form-field', _jsxs(_Fragment, { children: [label(field.required), refusal, _jsx(Still, { edit: still, children: input })] }));
}
/** The wording of a consent: the API's text, or what the editor puts in its place. */
function wording(offer, edit) {
    return edit?.consent ? edit.consent(offer) : offer.rendered_text;
}
/** What a box posts: the wording, and for a sponsor consent the sponsor after a colon. */
function consentValue(offer) {
    return offer.sponsor
        ? `${offer.consent_template_version_id}:${offer.sponsor.sponsor_id}`
        : offer.consent_template_version_id;
}
/** Id of a consent box, the target of the error summary's link. */
export function consentInputId(offer) {
    return `consent-${consentValue(offer).replace(':', '-')}`;
}
/**
 * The first box of the form that is required, asked and not among `ticked`, in the order the
 * boxes stand: the one a refusal for a missing consent points at (D51).
 */
export function missingConsent(consents, ticked, blockOrder) {
    for (const block of formBlockOrder(blockOrder)) {
        const offer = consents.find((entry) => entry.consent_type === block &&
            entry.mandatory &&
            !entry.already_granted &&
            !ticked.includes(consentValue(entry)));
        if (offer !== undefined) {
            return offer;
        }
    }
    return null;
}
/**
 * One consent, required or optional (D52): the wording is the box's label, with the star when
 * the action requires the kind. It posts its template version, and a sponsor consent its
 * sponsor after a colon, required or not (D51: a required sponsor consent without one is
 * refused).
 *
 * NO LINK INSIDE THE LABEL: a tap on anything inside a `<label>` toggles the box, as the terms
 * line below explains. The released wordings end with the privacy address (`Datenschutz:
 * {privacy_policy_url}`), so the wording gets the terms line's cut at `privacyHref`: the text
 * before it and the rest with the star are two labels of the same box, the address between them
 * is the link. Without the address in the wording, and in the editor, one label as before.
 */
function ConsentBox({ offer, edit, ticked, missing, privacyHref, }) {
    const still = edit !== undefined;
    const value = consentValue(offer);
    const id = consentInputId(offer);
    const text = offer.rendered_text;
    const at = still || !privacyHref ? -1 : text.indexOf(privacyHref);
    const star = offer.mandatory ? _jsx(Required, {}) : null;
    return (_jsxs("div", { className: "countdown-consent", children: [_jsx(Still, { edit: still, children: _jsx("input", { type: "checkbox", id: id, name: "consent", value: value, required: offer.mandatory, defaultChecked: ticked.includes(value), "aria-invalid": id === missing ? true : undefined }) }), privacyHref && at >= 0 ? (_jsxs("span", { className: "countdown-consent__text", children: [_jsx("label", { htmlFor: id, children: text.slice(0, at) }), _jsx("a", { href: privacyHref, target: "_blank", rel: "noopener", children: privacyHref }), _jsxs("label", { htmlFor: id, children: [text.slice(at + privacyHref.length), star] })] })) : (_jsxs(BoxTitle, { htmlFor: id, still: still, children: [wording(offer, edit), star] }))] }));
}
/**
 * The terms box in one line. Its name comes from the whole line (`aria-labelledby`), so a screen
 * reader says "Ich akzeptiere die Teilnahmebedingungen." on the box itself, and "required".
 *
 * The link stays OUTSIDE the labels on purpose: inside one, a click would toggle the checkbox
 * instead of following the link, and the terms would be unreachable for exactly the people who
 * wanted to read them before agreeing. So the line is cut at the word »Teilnahmebedingungen«:
 * the text before and after it are two labels of the same box, the word is the link. A line the
 * action words without that word gets the link after it.
 */
function TermsLine({ text, href, target, }) {
    const link = (_jsx("a", { href: href, target: target, rel: target === undefined ? undefined : 'noopener', children: TERMS_WORD }));
    const at = text.indexOf(TERMS_WORD);
    if (at < 0) {
        return (_jsxs(_Fragment, { children: [_jsxs("label", { htmlFor: "terms", children: [text, _jsx(Required, {})] }), ' ', link] }));
    }
    return (_jsxs(_Fragment, { children: [_jsx("label", { htmlFor: "terms", children: text.slice(0, at) }), link, _jsxs("label", { htmlFor: "terms", children: [text.slice(at + TERMS_WORD.length), _jsx(Required, {})] })] }));
}
export function CompletionForm(props) {
    const { signedIn, deviceRecognised, headline, intro, fields, reusedOn, consents, typed, prefill = {}, ticked = [], missing = null, problems, edit, } = props;
    const still = edit !== undefined;
    const details = !signedIn || fields.length > 0 || reusedOn !== null;
    const text = (key) => props.texts?.[key] ?? FORM_TEXT_DEFAULTS[key];
    /** An option text: the host's editor in the preview, the action's words on the page. */
    const option = (key, multiline = false) => edit ? edit.text(key, FORM_TEXT_DEFAULTS[key], multiline) : text(key);
    /**
     * The boxes of one kind, required or optional (D51). One granted before is not asked again
     * and posts nothing: the API counts the earlier grant for a required kind.
     */
    const consentBlock = (kind) => {
        const offers = consents.filter((offer) => offer.consent_type === kind && !offer.already_granted);
        return offers.length > 0 ? (_jsx(_Fragment, { children: offers.map((offer) => (_jsx(ConsentBox, { offer: offer, edit: edit, ticked: ticked, missing: missing, privacyHref: props.privacyHref }, consentValue(offer)))) })) : null;
    };
    const askedFields = fields.length > 0 || reusedOn !== null || edit?.fields?.after !== undefined;
    const content = {
        email: signedIn ? null : (_jsxs("p", { className: "countdown-form-field", children: [_jsxs(BoxTitle, { htmlFor: "email", still: still, className: "countdown-form-field__label", children: [option('email_label'), _jsx(Required, {})] }), _jsx(Still, { edit: still, children: _jsx("input", { className: "countdown-form-field__input", type: "email", id: "email", name: "email", defaultValue: typed.email ?? '', autoComplete: "email", maxLength: 320, "aria-describedby": "hinweis-email", required: true }) }), _jsx("span", { id: "hinweis-email", className: "countdown-subheading", children: deviceRecognised ? RECOGNISED_HINT : option('email_hint', true) })] })),
        lead_fields: askedFields ? (_jsxs(_Fragment, { children: [reusedOn !== null ? (_jsxs("p", { className: "countdown-form-field__hint", children: ["Wir verwenden Ihre Angaben vom ", reusedOn, "."] })) : null, fields.map((field) => {
                    const input = (_jsx(FieldInput, { field: field, value: typed[inputName(field.key)] ?? prefill[field.key] ?? '', problem: problems[field.key], edit: edit }));
                    const frame = edit?.fields?.frame;
                    return (_jsx(Fragment, { children: frame
                            ? frame(field.key, input, fields.map((entry) => entry.key))
                            : input }, field.key));
                }), edit?.fields?.after] })) : null,
        terms: (_jsxs("div", { className: "countdown-consent", children: [_jsx(Still, { edit: still, children: _jsx("input", { type: "checkbox", id: "terms", name: "terms", value: TERMS_ACCEPTED, "aria-labelledby": "zeile-terms", defaultChecked: ticked.includes('terms'), required: true }) }), _jsx("span", { id: "zeile-terms", className: "countdown-consent__text", children: edit ? (_jsxs(_Fragment, { children: [option('terms_label'), _jsx(Required, {})] })) : (_jsx(TermsLine, { text: text('terms_label'), href: props.termsHref, target: props.termsTarget })) })] })),
        registration: consentBlock('registration'),
        account: !signedIn && props.accountOffered ? (_jsxs("div", { className: "countdown-consent", children: [_jsx(Still, { edit: still, children: _jsx("input", { type: "checkbox", id: "konto", name: "konto", value: "ja", "aria-describedby": "hinweis-konto" }) }), _jsxs("span", { className: "countdown-consent__text", children: [_jsx(BoxTitle, { htmlFor: "konto", still: still, children: option('account_title') }), _jsx("span", { id: "hinweis-konto", className: "countdown-consent__hint", children: option('account_hint', true) })] })] })) : null,
        newsletter: consentBlock('newsletter'),
        sponsor_contact: consentBlock('sponsor_contact'),
        daily_reminder: consentBlock('daily_reminder'),
    };
    const shown = formBlockOrder(props.blockOrder).filter((block) => content[block] !== null);
    return (_jsxs("form", { method: "post", action: props.action, className: props.bare === true ? undefined : 'countdown-panel', children: [_jsx("input", { type: "hidden", name: "field_position", value: props.fieldPosition }), _jsx("input", { type: "hidden", name: "terms_version_id", value: props.termsVersionId }), !signedIn ? (_jsxs("div", { "aria-hidden": "true", style: {
                    position: 'absolute',
                    left: '-10000px',
                    width: '1px',
                    height: '1px',
                    overflow: 'hidden',
                }, children: [_jsx("label", { htmlFor: "zusatz", children: "Erg\u00E4nzende Anmerkung" }), _jsx("input", { type: "text", id: "zusatz", name: "additional_note", tabIndex: -1, autoComplete: "off" })] })) : null, _jsxs("fieldset", { className: "countdown-fieldset", children: [details && (edit !== undefined || headline) ? (_jsx("legend", { className: "countdown-legend", children: edit ? edit.text('headline', '') : headline })) : null, edit ? (_jsx("p", { className: "countdown-subheading", children: edit.text('intro', '', true) })) : intro ? (_jsx("p", { className: "countdown-subheading", children: intro })) : null, shown.map((block) => (_jsx(Fragment, { children: edit?.block ? edit.block(block, content[block], shown) : content[block] }, block)))] }), _jsx("p", { className: "countdown-form-note", "aria-hidden": "true", children: "* Pflichtangabe" }), edit ? (_jsx("div", { className: "countdown-button", style: { marginTop: 'var(--countdown-space-4)' }, children: edit.text('submit_label', FORM_TEXT_DEFAULTS.submit_label) })) : (_jsx("button", { className: "countdown-button", type: "submit", style: { marginTop: 'var(--countdown-space-4)' }, children: props.submitLabel ?? FORM_TEXT_DEFAULTS.submit_label }))] }));
}
//# sourceMappingURL=CompletionForm.js.map